import type React from "react";
import "./CurrentTenantPaymentList.css";
import {
  ContractorService,
  EntityType,
  PaymentDueAccountVM,
  PaymentMethodTypesEnum,
  PaymentMethodVM,
  PaymentService,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import deleteIcon from "../../assets/componentsIcons/dropdown/delete.svg";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import TruncatedDescriptionCell from "../common/TruncatedDescriptionCell";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import SelectField from "../form-fields/SelectField";
import InputField from "../form-fields/InputField";
import { UserLoginData } from "../../components/common/charts/Enums";
import ActionMenu from "../actionMenus/action-menu";
import { utcDateFormat } from "../../utils/helper";
import DateHelper from "../../utils/DateHelper";
import UtilHelper from "../../utils/UtilHelper";
import H2 from "../typography/H2";
import SimpleSpinner from "../../components/loader";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import { GetImages } from "../../utils/GetImages";
import EmptyState from "../emptystate";
import BluePayableIcon from "../../assets/Payment-Icon.svg";
import { getEnumOptions } from "../../utils/enumHelpers";

// Recording modes — same set as Service Request / Payables post (no Assisted).
const PAYMENT_MODE = PaymentMethodTypesEnum;
const PAYMENT_MODE_OPTIONS = getEnumOptions(PAYMENT_MODE, [
  PAYMENT_MODE.Plaid,
  PAYMENT_MODE.Cash,
  PAYMENT_MODE.Check,
  PAYMENT_MODE.Other,
]);

export interface Payment {
  id?: number;
  payerPayeeName?: string | null;
  description?: string | null;
  dueDate?: string;
  amount?: number;
  streetAddress?: string | null;
  isEntity?: boolean;
  entityId?: number;
  entityTypeId?: number;
  className?: string;
  detailsActiveTab?: string;

  refreshTrigger?: number;
  onRefresh?: () => void;

  expensesDueRaw?: number | string;
  expensesDue?: number | string;
}

const ContractorPaymentList: React.FC<Payment> = ({
  isEntity,
  entityId,
  entityTypeId,
  className,
  detailsActiveTab,
  refreshTrigger,
  onRefresh,
  expensesDue,
  expensesDueRaw
}) => {
  const { id: contractorId } = useParams();
  const [payments, setPayments] = useState<PaymentDueAccountVM[]>([]);
  const [plaidPaymentMethods, setPlaidPaymentMethods] = useState<
    { Id: string; Name: string }[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [contractorData, setContractorData] = useState<any>(null);
  const [selectedPayment, setSelectedPayment] = useState<PaymentDueAccountVM | null>(null);
  const [isBulk, setIsBulk] = useState(false);

  const currencySymbol = useCurrencySymbol();
  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      paymentMethodTypeId: "",
      mode: "",
      date: new Date().toISOString().split("T")[0],
      checkNo: "",
      transactionId: "",
    },
  });
  const navigate = useNavigate();
  const formId = "post-payment-form";

  const selectedPaymentModeType = Number(
    useWatch({ control: methods.control, name: "paymentMethodTypeId" }),
  );
  const isCheck = selectedPaymentModeType === PAYMENT_MODE.Check;
  const showTransactionId = selectedPaymentModeType === PAYMENT_MODE.Other;

  const {
    formState: { isDirty, isSubmitting, isValid },
  } = methods;

  // Menu actions — Post always; Delete only for non–service-request dues
  // (SR-linked PDAs must be settled from the service request — bug/sr-pda-undeletable).
  const getMenuItems = (payment: PaymentDueAccountVM) => {
    const postItem = {
      icon: BluePayableIcon,
      label: "Post Payment",
      action: () => {
        setSelectedPayment(payment);
        setModalOpen(true);
      },
      isDivider: false,
      isDestructive: false
    };

    if (payment.entityTypeId === EntityType.ServiceRequest) {
      return [postItem];
    }

    return [
      postItem,
      { isDivider: true },
      {
        icon: deleteIcon,
        label: "Delete",
        action: () => {
          setSelectedPayment(payment);
          setShowConfirmation("delete");
        },
        isDivider: false,
        isDestructive: false
      },
    ];
  };

  // Reset conditional fields when payment mode changes
  useEffect(() => {
    methods.resetField("checkNo");
    methods.resetField("transactionId");
    methods.resetField("mode");
  }, [selectedPaymentModeType]);

  const deletePayment = async () => {
    if (!selectedPayment?.id || isDeleting) return;

    setIsDeleting(true);
    try {
      await apiHelper(
        PaymentService.deleteApiPaymentDueAccount,
        {
          successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
          showNotification: true,
          successMessage: "Payment Deleted Successfully",
        },
        selectedPayment.id
      );
      getPayments();
      setShowConfirmation(null);
      setSelectedPayment(null);

      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Delete Payment Error:", err);
      setShowConfirmation(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchCurrentContractorDetails = () => {
    if (contractorId) {
      apiHelper(ContractorService.getApiContractor, { showNotification: false }, contractorId)
        .then((res) => {
          if (res?.data?.success) {
            setContractorData(res.data.success);
          }
        })
        .catch((err) => {
          console.error("Error fetching contractor:", err);
        });
    }
  };

  const getPaymentsMode = async () => {
    try {
      const orgId = parseInt(
        sessionStorage.getItem(UserLoginData.organization_id) || "0"
      );
      const res = await apiHelper(
        PaymentService.getApiPaymentMethodOrganizationlist,
        { showNotification: false },
        orgId
      );
      const rawItems: PaymentMethodVM[] = res?.data?.success || [];
      // Secondary Plaid account picker only — recording modes come from the enum
      // (Cash/Check/Other/Plaid), matching Service Request / Payables post.
      setPlaidPaymentMethods(
        rawItems
          .filter((item) => item.paymentMethodTypeId === PAYMENT_MODE.Plaid)
          .map((item) => ({
            Id: String(item.id),
            Name: item.accountName || `Account ${item.id}`,
          })),
      );
    } catch (err) {
      console.error("Payments API Error:", err);
      setPlaidPaymentMethods([]);
    }
  };

  const getPayments = async () => {
    if (!contractorId) return;
    setLoading(true);
    try {
      const res = await apiHelper(
        PaymentService.getApiPaymentDueAccountContractorlist,
        { showNotification: false },
        contractorId
      );
      const rawItems = res?.data?.success || [];



      if (rawItems.length) {
        setPayments(rawItems);



      } else {
        setPayments([]);
      }
    } catch (err) {
      console.error("Contractor Payments API Error:", err);
    } finally {
      setLoading(false);
    }
  };


  const handleSinglePaymentOpen = (payment: PaymentDueAccountVM) => {
    setIsBulk(false);
    setSelectedPayment(payment);
    setModalOpen(true);
  };

  const getNumericAmount = (amount: any): number => {
    if (typeof amount === 'number') return amount;
    if (typeof amount === 'string') {
      // Remove currency symbols and commas, then parse
      return parseFloat(amount.replace(/[^0-9.-]+/g, "")) || 0;
    }
    return 0;
  };

  const postPayments = async (formData: any) => {
    try {
      const paymentDate = utcDateFormat(formData.date);
      const paymentMethodTypeId = Number(formData.paymentMethodTypeId);

      const buildPayload = (payDueAccountId: number, amount: number | undefined) => {
        const payload: Record<string, unknown> = {
          payDueAccountId,
          amount,
          paymentMethodTypeId,
          paymentDate,
        };
        if (paymentMethodTypeId === PAYMENT_MODE.Plaid) {
          payload.fromPaymentMethodId = Number(formData.mode);
        } else if (paymentMethodTypeId === PAYMENT_MODE.Check) {
          payload.checkNumber = formData.checkNo || "";
        } else if (paymentMethodTypeId === PAYMENT_MODE.Other) {
          payload.transactionId = formData.transactionId || "";
        }
        return payload;
      };

      if (isBulk) {
        for (const payment of payments) {
          await apiHelper(
            PaymentService.putApiPaymentDueAccountPay,
            { showNotification: false },
            buildPayload(Number(payment.id), payment.amount),
          );
        }
        PubSub.publish(TopicsEnum.Notification.toString(), {
          message: "All payments posted successfully.",
          type: NotificationType.Success,
          icon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
        });
      } else {
        if (!selectedPayment) return;
        await apiHelper(
          PaymentService.putApiPaymentDueAccountPay,
          {
            showNotification: true,
            successMessage: "Payment Posted Successfully",
            successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
          },
          buildPayload(Number(selectedPayment.id), selectedPayment.amount),
        );
      }

      getPayments();
      setModalOpen(false);
      setIsBulk(false);
      setSelectedPayment(null);
      methods.reset();

      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Post Payment Error:", err);
    }
  };



  useEffect(() => {
    if (contractorId) {
      getPayments();
      getPaymentsMode();
      fetchCurrentContractorDetails();
    }
  }, [contractorId]);

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      getPayments();
    }
  }, [refreshTrigger]);


 if (loading) {
    return (
     <SimpleSpinner myClassName={"Icon50"} isFullPage={true} />
    );
  }

  if (payments.length === 0) {
    return (
      <div className="content-wrapper container-fluid">
        <EmptyState
          icon={GetImages.emptyStatePayment}
          heading="No Payments Found"
          isButton={false}
        />
      </div>

    );
  }

  return (
    <>
        <div className="payments-content">
          <div className="payments-header">
            <H2
              text="Payments"
            />
            {/* <SimpleButton
              label="Add Payment"
              onClick={() => {
                navigate("/payment/create", {
                  state: {
                    isEntityProp: isEntity,
                    entityIdProp: entityId,
                    entityTypeIdProp: entityTypeId,
                    from: window.location.pathname,
                    activeTab: detailsActiveTab,
                  },
                });
              }}
            /> */}
          </div>
          <div className="table-container">

            <table className="payments-table">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Description</th>
                  <th>Due Date</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {payments.length ? (
                  payments.map((payment) => (
                    <tr key={payment.id}>
                      <td>{payment.streetAddress || "-"}</td>
                      <td><TruncatedDescriptionCell description={payment.description} /></td>
                      <td>
                        {DateHelper.formatISOToMMDDYYYY(payment.dueDate) || "-"}
                      </td>
                      <td>
                        <CurrencyTooltip
                          content={payment.formatted?.AmountRaw || 0}
                          formatted={payment.formatted?.Amount}
                        >
                          <span>
                            {payment.formatted?.Amount ||
                              "-"}
                          </span>
                        </CurrencyTooltip>
                      </td>
                      <td>
                        <ActionMenu
                          menuItems={getMenuItems(payment)}
                          trigger={
                            <button
                              className="action-button"
                              aria-label="More options"
                            >
                              <img
                                src={dropdownIcon}
                                alt="More options"
                                width={20}
                                height={20}
                                className="action-icon"
                              />
                            </button>
                          }
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center" }}>
                      No payments found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

          </div>

          {
            payments.length > 0 && (
              <div className="total-amount-card">
                <div className="total-amount-content" style={{ justifyContent: "space-between" }}>
                  <div>
                    <span className="total-label">Total Amount Due</span>
                  </div>



                  <CurrencyTooltip
                    content={expensesDueRaw}
                    formatted={expensesDue}
                  >
                    <span className="total-value">{expensesDue}</span>
                  </CurrencyTooltip>
                </div>
              </div>
            )
          }


        </div>
     

      {/* Delete Confirmation */}
      {showConfirmation === "delete" && selectedPayment && (
        <ConfirmationPopup
          isOpen={true}
          title="Delete Payment"
          message={`Do you want to delete this payment of ${currencySymbol}${selectedPayment?.amount?.toFixed(2)}?`}
          primaryButtonLabel="Delete"
          secondaryButtonLabel="Cancel"
          onPrimaryAction={deletePayment}
          onSecondaryAction={() => setShowConfirmation(null)}
          onClose={() => setShowConfirmation(null)}
          isPrimaryDestructive={true}
          isLoading={isDeleting}
          loadingLabel="Deleting..."
        />
      )}

      {/* Post Payment Modal */}
      {isModalOpen && (
        <RightSideModal
          isOpen={isModalOpen}
          onClose={() => { setModalOpen(false); setIsBulk(false); }}
          title={isBulk ? "Post All Payments" : "Post Payment"}
          buttonlabel="Save"
          formId={formId}
          maxWidth={550}
          isLoading={isSubmitting}
          isDirty={isDirty}
          isSubmitDisabled={isSubmitting || !isDirty || !isValid}
        >
          <Card className="no-bg">




            <div className="tenant-info">
              {contractorData && selectedPayment?.amount && (
                <>
                  <div className="tenant-left">
                    <div className="tenant-avatar">
                      <div className="tenant-initials">
                        {UtilHelper.getInitials(
                          contractorData?.firstName + " " + contractorData?.lastName
                        )}
                      </div>
                    </div>
                    <div className="tenant-name">
                      {contractorData?.firstName + " " + contractorData?.lastName}
                      <p className="tenant-payment-type">  {isBulk ? `Contractor Payments` : "Contractor Payment"}</p>
                    </div>
                  </div>
                  <div className="tenant-amount">
                    {currencySymbol}{getNumericAmount(selectedPayment?.amount).toFixed(2)}
                  </div>
                </>
              )}
            </div>

            <FormProvider {...methods}>
              <form id={formId} onSubmit={methods.handleSubmit(postPayments)}>
                <div className="row">
                  <div className="col-md-6">
                    <SelectField
                      name="paymentMethodTypeId"
                      label="Payment Mode"
                      required
                      requiredMessage="Payment mode is required"
                      options={PAYMENT_MODE_OPTIONS}
                      placeholderText="Select Mode"
                    />
                  </div>
                  <div className="col-md-6">
                    <InputField name="date" label="Payment Date" type="date" required requiredMessage="Payment Date is required" />
                  </div>
                </div>

                {selectedPaymentModeType === PAYMENT_MODE.Plaid && (
                  <SelectField
                    name="mode"
                    label="Payment Method"
                    required
                    requiredMessage="Please select a payment method"
                    options={plaidPaymentMethods}
                    placeholderText="Select a payment method"
                  />
                )}

                {isCheck && (
                  <InputField
                    name="checkNo"
                    label="Enter Check No"
                    type="text"
                    required={true}
                    requiredMessage="Please enter a check number"
                    placeholder="Enter check number"
                    onlyNumeric={true}
                  />
                )}

                {showTransactionId && (
                  <InputField
                    name="transactionId"
                    label="Transaction ID"
                    type="text"
                    required={true}
                    requiredMessage="Please enter a transaction ID"
                    placeholder="Enter transaction ID"
                  />
                )}
              </form>
              <div className="payment-alert">
                <p>Payment Amount:</p>
                <p className="payment-divider">------------------------</p>
                <p style={{ fontWeight: "bold" }}>
                  {currencySymbol}{getNumericAmount(selectedPayment?.amount).toFixed(2)}
                </p>
              </div>
            </FormProvider>
          </Card>
        </RightSideModal>
      )}
    </>
  );
};

export default ContractorPaymentList;